import React from 'react';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock';
import LockUnlockedIcon from '@atlaskit/icon/core/migration/lock-unlocked--unlock';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import type { Size } from '@atlaskit/icon/src';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types';
import { shouldApplyPermissionsGate } from '@atlassian/jira-common-selectors/src/edition';
import { fg } from '@atlassian/jira-feature-gating';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { AccessType } from '@atlassian/jira-project-access/types';
import {
	getApplicationForProject,
	type Application,
} from '@atlassian/jira-shared-types/src/application.tsx';
import { FREE_ACCESS_LEVEL } from '@atlassian/jira-shared-types/src/project.tsx';
import { ACCESS_TYPES } from '../constants';
import { multipleRolesText } from '../messages';
import type { Actor, AccessPageContext, RoleId, ActorType } from '../types';
import type { AnalyticsTrigger } from './types';

/**
 * @deprecated GasV2 has been cleaned up, and it has not been logging any events since July. Please replace implementation with sending GasV3 analytics: go/gasv3
 */
const triggerAnalyticsForApp =
	(_appName: string) =>
	(_eventNameSuffix: string, _eventData: unknown): Promise<void> =>
		Promise.resolve(undefined);

/**
 * @deprecated GasV2 has been cleaned up, and it has not been logging any events since July. Please replace implementation with sending GasV3 analytics: go/gasv3
 *
 * Exports a function that will automatically namespace any triggered
 * events with the String `users-and-roles`.
 * See {@link /common/util/analytics/trigger-analytics.js} for the complete details.
 *
 * Params of the exported function:
 * @param String eventName of the analytics event
 * @param Object data to be sent alongside the event
 */
export const triggerAnalytics: AnalyticsTrigger = triggerAnalyticsForApp('users-and-roles');

const ICON_SIZE: Size = 'medium';

export const getIcon = (icon: string, label = '', noColour = false) => {
	const localProps = {
		label,
		color: noColour ? token('color.icon') : token('color.icon.accent.gray', colors.N100),
		LEGACY_size: ICON_SIZE,
		spacing: 'spacious' as const,
	};
	switch (icon) {
		case ACCESS_TYPES.OPEN:
			// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
			return <LockUnlockedIcon {...localProps} />;
		case ACCESS_TYPES.LIMITED:
			return (
				// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
				<PeopleIcon
					label={localProps.label}
					primaryColor={localProps.color}
					size={localProps.LEGACY_size}
				/>
			);
		case ACCESS_TYPES.PRIVATE:
			// eslint-disable-next-line @atlaskit/design-system/ensure-icon-color
			return <LockLockedIcon {...localProps} />;
		default:
			return (
				// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
				<PeopleIcon
					label={localProps.label}
					primaryColor={localProps.color}
					size={localProps.LEGACY_size}
				/>
			);
	}
};

export const isActorInvitedUser = (actor: Actor): boolean => actor.type === 'invited-user';

export const isUser = (type: ActorType): boolean => type === 'atlassian-user-role-actor';

export const applicationSelector = (context: AccessPageContext): Application => {
	const { projectType } = context;
	return getApplicationForProject(projectType);
};

export const hasPermissionsGate = (context: AccessPageContext): boolean => {
	const edition = context.appEdition;
	const application = applicationSelector(context);
	return shouldApplyPermissionsGate(application, edition);
};

export const isDowngradedEdition = (currentSelect: AccessType): boolean =>
	(currentSelect && currentSelect.value) !== FREE_ACCESS_LEVEL;

export const getActorRoleIds = (actor: Actor): RoleId[] => actor.roles.map(({ id }): RoleId => id);
export const multipleRolesMessage = (intl: IntlShape, count: number): string =>
	intl.formatMessage(multipleRolesText.roleMessage, { count });

export const getMsgKeyByProjectType = (messageKey: string, projectType: string): string =>
	// eslint-disable-next-line no-nested-ternary
	projectType === SERVICE_DESK_PROJECT
		? fg('jsm_simplified_project_settings_navigation')
			? `jsdTMP${messageKey}`
			: `jsd${messageKey}`
		: messageKey;

export const noopPromise = (): Promise<void> => Promise.resolve();

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
